import { useEffect, useState } from "react";
import { ToWords } from "to-words";
import { fetchTransactionById } from "../../apis/transaction";
import DownloadButton from "../DownloadButton";
import {
  capitalizeFirstLetterOfEachWord,
  convertAmountToWords,
} from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";

export const POInvoiceLayout = ({
  headDetails,
  companyData,
  itemDetails,
  rowData,
  invoiceTableData,
  selectedReportName,
}) => {
  const toWords = new ToWords();
  const path = window.location.pathname.split("/").filter(Boolean);
  const transactionName = selectedReportName || path[1];

  const organizationData = JSON.parse(localStorage.getItem("organisation"));
  const [poDocument, setPoDocument] = useState(null);
  const transactionId = headDetails.tran_id;

  useEffect(() => {
    let active = true;
    setPoDocument(null);
    const load = async () => {
      try {
        if (!transactionId)
          throw new Error("Save the Purchase Order before printing.");
        const po = await fetchTransactionById("PO", transactionId);
        const apiError = po?.description || po?.error?.description;
        const quotes = po?.purchase_order_document?.vendor_quotes;
        if (apiError || !Array.isArray(quotes))
          throw new Error(
            apiError || "Purchase Order document data is unavailable.",
          );
        if (active) setPoDocument({ data: { vendorQuotes: quotes } });
      } catch (error) {
        console.error("Failed to load PO document data:", error);
        if (active) setPoDocument({ error: error.message });
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [transactionId]);
  const poData = poDocument?.data;

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const notes = [
    "Test certificate / inspection report must accompany material.",
    "Guarantee / Warranty certificates",
    "Please courier original MODVAT copies of invoices directly to us & not with transporter.",
  ];

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const formatCompanyAddress = (companyData) => {
    if (!companyData) return "N/A";

    const address = companyData.address?.trim();
    const city = companyData.city?.trim();
    const pin = companyData.pincode ? `- ${companyData.pincode}` : "";
    const location = [city, pin].filter(Boolean).join(" ");

    return address || location ? (
      <>
        {address}
        {address && location ? (city ? ", " : " ") : ""}
        {location && (
          <span className="purchase-order-company-location">{location}</span>
        )}
      </>
    ) : (
      "N/A"
    );
  };

  const visibleFields = [...rowData]
    .filter((field) => !field.is_internal_field)
    .reverse();

  const calculateTaxFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalGst: 0,
        cgstPercent: 0,
        sgstPercent: 0,
        igstPercent: 0,
      };
    }

    const taxTotals = itemDetails.reduce(
      (totals, item) => {
        const cgstAmt = parseFloat(item.cgst_amt || 0);
        const sgstAmt = parseFloat(item.sgst_amt || 0);
        const igstAmt = parseFloat(item.igst_amt || 0);
        const cgstPer = parseFloat(item.cgst_per || 0);
        const sgstPer = parseFloat(item.sgst_per || 0);
        const igstPer = parseFloat(item.igst_per || 0);

        return {
          cgst: totals.cgst + cgstAmt,
          sgst: totals.sgst + sgstAmt,
          igst: totals.igst + igstAmt,
          cgstPer: totals.cgstPer + cgstPer,
          sgstPer: totals.sgstPer + sgstPer,
          igstPer: totals.igstPer + igstPer,
        };
      },
      { cgst: 0, sgst: 0, igst: 0, cgstPer: 0, sgstPer: 0, igstPer: 0 },
    );

    const totalGst = taxTotals.cgst + taxTotals.sgst + taxTotals.igst;

    const itemCount = itemDetails.length;
    const cgstPercent =
      itemCount > 0 ? (taxTotals.cgstPer / itemCount).toFixed(2) : 0;
    const sgstPercent =
      itemCount > 0 ? (taxTotals.sgstPer / itemCount).toFixed(2) : 0;
    const igstPercent =
      itemCount > 0 ? (taxTotals.igstPer / itemCount).toFixed(2) : 0;

    return {
      cgst: taxTotals.cgst.toFixed(2),
      sgst: taxTotals.sgst.toFixed(2),
      igst: taxTotals.igst.toFixed(2),
      totalGst: totalGst.toFixed(2),
      cgstPercent,
      sgstPercent,
      igstPercent,
    };
  };

  const calculateTotalFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return 0;
    }

    const totalAmount = itemDetails.reduce((sum, item) => {
      const amount = parseFloat(item.amount || 0);
      return sum + amount;
    }, 0);

    return totalAmount;
  };

  const { cgst, sgst, igst, totalGst, cgstPercent, sgstPercent, igstPercent } =
    calculateTaxFromItems();
  const totalAmountFromItems = calculateTotalFromItems();
  const finalTotal = Number(
    (totalAmountFromItems + parseFloat(totalGst)).toFixed(2),
  );
  const amountInWords = convertAmountToWords(finalTotal, toWords);
  const summaryCells = [
    {
      taxLabel: `CGST @ ${cgstPercent}%`,
      taxValue: formatCurrency(cgst),
    },
    {
      taxLabel: `SGST @ ${sgstPercent}%`,
      taxValue: formatCurrency(sgst),
    },
    {
      taxLabel: `IGST @ ${igstPercent}%`,
      taxValue: formatCurrency(igst),
    },
    {
      taxLabel: "Net Amount (Before GST)",
      taxValue: formatCurrency(totalAmountFromItems || 0),
    },
    {
      taxLabel: "Total GST Amount",
      taxValue: formatCurrency(totalGst),
    },
  ];

  return (
    <div className="invoice-container sales-purchase-order-layout purchase-order-page-container">
      <div className="invoice-box">
        <div className="invoice-left">
          <div className="invoice-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div>
            <h3 className="invoice-company-name">
              {organizationData.name || "N/A"}
            </h3>
            <p className="invoice-company-address">
              {formatCompanyAddress(organizationData) || "N/A"}
              <br />
              GSTIN: {organizationData.gst_no || "N/A"}
              <br />
              State: {organizationData.state || "N/A"}
              <br />
              Email: Purchase@finepunch.in
              <br />
              Website: {organizationData.website || "www.finepunch.in"}
            </p>
          </div>
        </div>

        <div className="purchase-order-heading">
          <h2 className="invoice-title">
            {capitalizeFirstLetterOfEachWord(transactionName || "")}
          </h2>
        </div>

        <div className="invoice-right">
          <div className="purchase-detail">
            <p className="detail-label">
              <strong>Purchase Order No:</strong>
            </p>
            <div className="detail-value">{headDetails.tran_no}</div>
          </div>
          <div className="purchase-detail">
            <p className="detail-label">
              <strong>Purchase Order Date:</strong>
            </p>
            <div className="detail-value">
              {formatDate(headDetails.date || headDetails.created_at)}
            </div>
          </div>
          <div className="purchase-detail">
            <p className="detail-label">
              <strong>Vendor Quote Ref:</strong>
            </p>
            <div className="detail-value">
              {poData?.vendorQuotes.map((quote) => (
                <div key={quote.tran_id}>{quote.vendor_quote_ref}</div>
              ))}
            </div>
          </div>
          <div className="purchase-detail">
            <p className="detail-label">
              <strong>Vendor Quote Date:</strong>
            </p>
            <div className="detail-value">
              {poData?.vendorQuotes.map((quote) => (
                <div key={quote.tran_id}>
                  {formatDate(quote.vendor_quote_date)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="invoice-supplier-details-container">
        <div className="invoice-supplier-section">
          <h4 className="invoice-section-title">Name of Supplier:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {companyData.company_name || "N/A"}
            </span>
            <br />
            Address: {companyData.company_address || "N/A"}
            <br />
            GSTIN: {companyData.company_gst_no || "N/A"}
          </p>
        </div>

        <div className="invoice-delivery-section">
          <h4 className="invoice-section-title">Delivery at:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {organizationData.name || "N/A"}
            </span>
            <br />
            GSTIN: {organizationData.gst_no || "N/A"}
            <br />
            Address:{" "}
            {formatCompanyAddress(organizationData) || "Address not specified"}
          </p>
        </div>
        <div className="invoice-terms-conditions invoice-terms-section">
          <p className="terms-heading">Terms & Conditions :</p>
          <p className="terms-line">
            Delivery Date :{" "}
            <span className="terms-value">
              {headDetails.expected_delivery
                ? formatDate(headDetails.expected_delivery)
                : "--"}
            </span>
          </p>
          <p className="terms-line">
            Mode of Dispatch :{" "}
            <span className="terms-value">
              {headDetails.mode_of_dispatch ?? "--"}
            </span>
          </p>
          <p className="terms-line">
            Payment :{" "}
            <span className="terms-value">{headDetails.payment ?? "--"}</span>
          </p>
          <p className="terms-line">
            Jurisdiction :{" "}
            <span className="terms-value">
              {headDetails.jurisdiction ?? "--"}
            </span>
          </p>
          <p className="terms-line">
            Freight :{" "}
            <span className="terms-value">{headDetails.freightt ?? "--"}</span>
          </p>
        </div>
      </div>

      <div className="invoice-overflow-x-scroll">
        <table className="invoice-table">
          <colgroup>
            <col style={{ width: "5%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead className="invoice-thead">
            <tr>
              <th
                className="invoice-th po-th-group"
                rowSpan={2}
                style={{ borderLeftWidth: "0px" }}
              >
                Sl. No.
              </th>
              <th className="invoice-th po-th-group" rowSpan={2}>
                Product Details
              </th>
              <th className="invoice-th po-th-group" rowSpan={2}>
                HSN Code
              </th>
              <th
                className="invoice-th po-th-group po-quantity-cell"
                rowSpan={2}
              >
                Qty
              </th>
              <th className="invoice-th po-th-group" rowSpan={2}>
                UOM
              </th>
              <th className="invoice-th po-th-group" rowSpan={2}>
                Rate [INR]
              </th>
              <th className="invoice-th po-th-group" rowSpan={2}>
                Amount [INR]
              </th>
              <th
                className="invoice-th po-th-group"
                colSpan={2}
                style={{ textAlign: "center" }}
              >
                CGST
              </th>
              <th
                className="invoice-th po-th-group"
                colSpan={2}
                style={{ textAlign: "center" }}
              >
                SGST
              </th>
              <th
                className="invoice-th po-th-group"
                colSpan={2}
                style={{ textAlign: "center" }}
              >
                IGST
              </th>
            </tr>
            <tr>
              <th className="invoice-th po-th-sub">%</th>
              <th className="invoice-th po-th-sub">Amt.</th>
              <th className="invoice-th po-th-sub">%</th>
              <th className="invoice-th po-th-sub">Amt.</th>
              <th className="invoice-th po-th-sub">%</th>
              <th
                className="invoice-th po-th-sub"
                style={{ borderRightWidth: "0px" }}
              >
                Amt.
              </th>
            </tr>
          </thead>
          <tbody>
            {itemDetails.map((item, index) => (
              <tr key={index}>
                <td className="invoice-td" style={{ borderLeftWidth: "0px" }}>
                  {index + 1}
                </td>
                <td className="invoice-td">
                  {item.pur_product_name || item.product_name || "-"}
                </td>
                <td className="invoice-td">
                  {item.hsn_code ??
                    item.hsn_no ??
                    item.hsn ??
                    item.hsnCode ??
                    item.product_hsn_code ??
                    "-"}
                </td>
                <td className="invoice-td po-quantity-cell">
                  {item.pur_quantity ?? item.quantity ?? "-"}
                </td>
                <td className="invoice-td">{item.uom || item.unit || "-"}</td>
                <td className="invoice-td">{item.rate || "-"}</td>
                <td className="invoice-td">{item.amount || "-"}</td>
                <td className="invoice-td">{item.cgst_per ?? "-"}</td>
                <td className="invoice-td">
                  {item.cgst_amt ?? item.cgst ?? "-"}
                </td>
                <td className="invoice-td">{item.sgst_per ?? "-"}</td>
                <td className="invoice-td">
                  {item.sgst_amt ?? item.sgst ?? "-"}
                </td>
                <td className="invoice-td">{item.igst_per ?? "-"}</td>
                <td className="invoice-td" style={{ borderRightWidth: "0px" }}>
                  {item.igst_amt ?? item.igst ?? "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <table
        className="purchase-order-totals-table"
        aria-label="Purchase order totals"
      >
        <tbody>
          <tr>
            {summaryCells.map((cell) => (
              <td key={cell.taxLabel} className="purchase-order-tax-cell">
                <p className="purchase-order-summary-label">{cell.taxLabel}</p>
                <p className="purchase-order-summary-value">{cell.taxValue}</p>
              </td>
            ))}
          </tr>
          <tr>
            <td colSpan={3} className="purchase-order-total-words">
              <strong>Total Amount: </strong>
              {`${amountInWords} Only`}
            </td>
            <td colSpan={2}>
              <div className="purchase-order-final-total">
                <strong>Total:</strong>
                <span>{formatCurrency(finalTotal)}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="invoice-grid-container">
        <div className="invoice-left-pane">
          <p
            className="invoice-note-heading"
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "12px",
            }}
          >
            <strong>Note: </strong>
          </p>
          <ul className="invoice-note-list">
            {notes.map((note, index) => (
              <li key={index} className="invoice-note-item">
                {note}
              </li>
            ))}
          </ul>
        </div>
        <div className="invoice-right-pane">
          <div className="invoice-footer-signature">
            <p style={{ margin: "0px", fontSize: "13px" }}>
              <strong>For, {organizationData.name}</strong>
            </p>
          </div>
        </div>
      </div>
      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>
      <div className="invoice-buttons" style={{ paddingTop: "40px" }}>
        <DownloadButton
          tableData={invoiceTableData}
          fileName="invoice"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".purchase-order-page-container"
        />
      </div>
    </div>
  );
};
